import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronRight, ChevronDown, Download, ExternalLink } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import DateFilter from "@/components/DateFilter";
import { downloadExcel } from "@/lib/download";
import BSDrillDialog from "@/components/BSDrillDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatCurrency(amount) {
  if (amount === undefined || amount === null) return "0.00";
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(absAmount);
  return amount < 0 ? `(${formatted})` : formatted;
}

function GroupSection({ items, colorClass, onDrill }) {
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (name) => {
    setExpandedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return items.map((group, gIdx) => {
    const isExpanded = expandedGroups[group.name];
    const hasChildren = group.children && group.children.length > 0;
    // Only offer party-wise drill-down for real BS sections — skip the
    // computed "Current Period Profit/Loss" plug rows that have no
    // ledger backing.
    const drillable = !!onDrill && !group.is_profit && !group.is_manual;

    return (
      <React.Fragment key={group.name || gIdx}>
        <TableRow
          className="cursor-pointer hover:bg-slate-50"
          data-testid={`bs-row-${group.name?.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <TableCell
            className="font-semibold"
            style={{ paddingLeft: "12px" }}
            onClick={() => hasChildren && toggleGroup(group.name)}
          >
            <div className="flex items-center gap-2">
              {hasChildren ? (
                isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                )
              ) : (
                <span className="w-4" />
              )}
              <span>{group.name}</span>
              {drillable && (
                <button
                  className="ml-1 p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-blue-600 transition"
                  onClick={(e) => { e.stopPropagation(); onDrill(group.name); }}
                  title="Party-wise drill-down"
                  data-testid={`bs-drill-btn-${group.name?.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </TableCell>
          <TableCell
            className={`text-right font-mono tabular-nums ${colorClass}`}
            onClick={() => hasChildren && toggleGroup(group.name)}
          >
            {formatCurrency(group.amount)}
          </TableCell>
        </TableRow>
        {isExpanded &&
          hasChildren &&
          group.children.map((child, cIdx) => (
            <TableRow
              key={`${gIdx}-${cIdx}`}
              className="hover:bg-slate-50"
            >
              <TableCell style={{ paddingLeft: "44px" }}>
                <span className={child.is_group ? "font-semibold text-sm" : "text-sm"}>
                  {child.name}
                </span>
              </TableCell>
              <TableCell className={`text-right font-mono tabular-nums text-sm ${colorClass}`}>
                {formatCurrency(child.amount)}
              </TableCell>
            </TableRow>
          ))}
      </React.Fragment>
    );
  });
}

export default function BalanceSheet({ companyContext }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  // Party-wise drill-down dialog state
  const [drill, setDrill] = useState({ open: false, name: "", side: "asset" });

  const companyId = companyContext?.companyId || "default";
  const branchId = companyContext?.branchId || null;

  const openDrill = (name, side) => setDrill({ open: true, name, side });
  const closeDrill = () => setDrill((d) => ({ ...d, open: false }));

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { ...filters, company_id: companyId };
        if (branchId) params.branch_id = branchId;
        const response = await axios.get(`${API}/reports/balance-sheet`, { params });
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, companyId, branchId]);

  if (error) {
    return (
      <div className="text-center py-8 text-red-600" data-testid="bs-error">
        {error}
      </div>
    );
  }

  return (
    <div data-testid="balance-sheet">
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="bs-title">
            Balance Sheet
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Click on any group to expand details
          </p>
        </div>
        <Button
          onClick={() => downloadExcel(
            `${API}/reports/balance-sheet/export`,
            { ...filters, company_id: companyId, ...(branchId ? { branch_id: branchId } : {}) },
            `Balance_Sheet_${new Date().toISOString().slice(0, 10)}.xlsx`,
          )}
          variant="outline"
          className="border-slate-300"
          data-testid="bs-download"
        >
          <Download className="w-4 h-4 mr-2" />
          Download Excel
        </Button>
      </div>

      <DateFilter onFilterChange={setFilters} />

      {loading ? (
        <div className="flex items-center justify-center h-64" data-testid="bs-loading">
          <div className="spinner"></div>
        </div>
      ) : (<>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Assets</p>
            <p className="text-xl font-bold text-blue-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(data?.total_assets)}
            </p>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 shadow-none">
          <CardContent className="p-4">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Liabilities</p>
            <p className="text-xl font-bold text-amber-600 mt-1 font-mono tabular-nums">
              &#8377; {formatCurrency(data?.total_liabilities)}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2 bg-blue-50">
            <CardTitle className="text-sm font-semibold text-blue-800">Assets</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="assets-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Account</TableHead>
                  <TableHead className="text-right font-semibold w-36">Amount (&#8377;)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <GroupSection items={data?.assets || []} colorClass="text-blue-600" onDrill={(n) => openDrill(n, "asset")} />
                {(!data?.assets || data.assets.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-slate-500 py-8">
                      No asset data available
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className="bg-blue-50 font-bold">
                  <TableCell>Total Assets</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-blue-700">
                    {formatCurrency(data?.total_assets)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card className="border border-slate-200 shadow-none">
          <CardHeader className="pb-2 bg-amber-50">
            <CardTitle className="text-sm font-semibold text-amber-800">Liabilities & Capital</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table data-testid="liabilities-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Account</TableHead>
                  <TableHead className="text-right font-semibold w-36">Amount (&#8377;)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <GroupSection items={data?.liabilities || []} colorClass="text-amber-600" onDrill={(n) => openDrill(n, "liability")} />
                {(!data?.liabilities || data.liabilities.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={2} className="text-center text-slate-500 py-8">
                      No liability data available
                    </TableCell>
                  </TableRow>
                )}
                <TableRow className="bg-amber-50 font-bold">
                  <TableCell>Total Liabilities</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-amber-700">
                    {formatCurrency(data?.total_liabilities)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      </>
      )}
      <BSDrillDialog
        open={drill.open}
        onClose={closeDrill}
        groupName={drill.name}
        companyId={companyId}
        branchId={branchId}
        fromDate={filters.from_date}
        toDate={filters.to_date}
        sideColor={drill.side}
      />
    </div>
  );
}
